import React, { createContext, useContext, useState, useCallback} from "react";
import {fetchPurchases, createPurchase, updatePurchase} from "./api/api";
import { useAuthenticatedEffect } from "./components/useauthenticatedeffect";

const PurchaseContext = createContext();

export const usePurchases = () => useContext(PurchaseContext);

export const PurchaseProvider = ({children}) => {
    const [purchases, setPurchases] = useState([]);
    const [totalPurchases, setTotalPurchases] = useState(0);
    const [error, setError] = useState('');
    const BIG_PAGE_SIZE = 1000; // set a large page size to minimize API calls esp in reports page

    const fetchAllPurchases = useCallback(async () => {
        try {
            const response = await fetchPurchases(1, {});
            setPurchases(response.results);
            setTotalPurchases(response.count);
        } catch (error) {
            console.error('Error fetching purchases:', error);
        }
    }, []);

    // added that fetches all purchases pages
    const fetchAllPurchasesAll = useCallback(async (filters = {}) => {
        try {
            let page = 1;
            let allResults = [];
            let hasNext = true;

            while (hasNext) {
                const response = await fetchPurchases(page, filters, BIG_PAGE_SIZE);
                if (!response || !response.results) break;

                allResults = [...allResults, ...response.results];

                if (response.next) {
                    page += 1;
                } else {
                    hasNext = false;
                }
            }

            setPurchases(allResults);
            setTotalPurchases(allResults.length);
            return allResults; // return all results if needed
        } catch (error) {
            console.error('Error fetching all purchases:', error);
            setError('Error fetching all purchases. Please try again.');
            return []; // return empty array on error
        }
    }, []);

    // use useAuthenticatedEffect instead of useEffect
    useAuthenticatedEffect(
        useCallback(() => {
            fetchAllPurchases().then(r => r); // added then(r => r) to remove warning
        }, [fetchAllPurchases])
    );

    const fetchPurchasesPaginated = useCallback(async (page = 1, filters = {}) => {
        try {
            const response = await fetchPurchases(page, filters);
            setPurchases(response.results);
            setTotalPurchases(response.count);
            setError('');
        } catch (error) {
            console.error('Error fetching purchases:', error);
            setError('Error fetching purchases. Please try again.');
            setPurchases([]);
        }
    }, []);

    const handleCreatePurchase =  async (newPurchase) => {
        const totalAmount = newPurchase.products.reduce((total, product) => {
            return total + product.quantity * product.purchase_price;
        }, 0);

        const purchaseData = {
            // ...newPurchase,
            supplier: newPurchase.supplier,
            purchase_date: newPurchase.purchase_date,
            invoice_number: newPurchase.invoice_number,
            has_return: newPurchase.has_return,
            total_amount: totalAmount,
            items: newPurchase.products.map((product) => {
                // ensuring that I only include units and conversion factors if they are applicable
                const itemData = {
                    product: product.productId,
                    quantity: product.quantity,
                    purchase_price: product.purchase_price,
                };

                if (product.unitId) {
                    itemData.unit = parseInt(product.unitId);
                    itemData.conversion_factor = parseFloat(product.conversion_factor) || 1.0;
                } else {
                    itemData.conversion_factor = 1.0; //default to 1.0 for base unit
                }
                return itemData;
            }),
        };

        try {
            const addedPurchase = await createPurchase(purchaseData);
            setPurchases((currentPurchases) => [addedPurchase, ...currentPurchases]);
            // await fetchAllPurchases();
            await fetchPurchasesPaginated();
        } catch (error) {
            console.error('Error adding purchase from context:', error);
            throw error; // re-throw so purchase.js can catch and display
        }
    };

    const handleUpdatePurchase = async (updatedPurchase) => {
        try {
            const updatedPurchaseData = await updatePurchase(updatedPurchase);
            setPurchases(purchases.map(purchase => purchase.id === updatedPurchaseData.id ? updatedPurchaseData : purchase));
        } catch (error) {
            console.error('Error updating purchase from context:', error);
        }
    };

    // const handleDeletePurchase = async (purchaseId) => {
    //     const confirmDelete = window.confirm('Are you sure you want to delete this purchase?');
    //     if (confirmDelete) {
    //         try {
    //             await deletePurchase(purchaseId);
    //             setPurchases(purchases.filter(purchase => purchase.id !== purchaseId));
    //         } catch (error) {
    //             console.error('Error deleting purchase:', error);
    //         }
    //     }
    // }

    return (
        <PurchaseContext.Provider value={{
            purchases,
            fetchAllPurchases,
            fetchAllPurchasesAll,
            createPurchase: handleCreatePurchase,
            updatePurchase: handleUpdatePurchase,
            totalPurchases,
            fetchPurchasesPaginated,
            error
        }}>
            {children}
        </PurchaseContext.Provider>
    );
};

